/**
 * Client-side mirror of the server password policy in
 * `server/app/services/passwords.py`. The server re-checks and is the authority,
 * answering 422 with a specific `detail`; this copy only drives the live
 * checklist and gates the submit button for instant feedback.
 *
 * `MIN_LENGTH`, the character-class rules and `SPECIAL_CHARS` must stay in sync
 * with the server's `_MIN_LENGTH`, its rules and `_SPECIALS`.
 */

const MIN_LENGTH = 12;

// A plain string with a membership test, not a regex, so the awkward
// characters (backtick, tilde, double quote, backslash) need no regex escaping
// and read the same as the server's set.
const SPECIAL_CHARS = "!@#$%^&*()_+-=[]{}|;:',.<>?/`~\"\\";

export interface PasswordRule {
  /** Stable key for React lists. */
  id: string;
  /** Human-readable requirement shown in the checklist. */
  label: string;
  /** Whether the candidate password satisfies this rule. */
  ok: boolean;
}

/** Evaluates each policy rule against `password` for the live checklist. */
export function evaluatePasswordRules(password: string): PasswordRule[] {
  return [
    { id: "length", label: `At least ${MIN_LENGTH} characters`, ok: password.length >= MIN_LENGTH },
    { id: "upper", label: "An uppercase letter", ok: /[A-Z]/.test(password) },
    { id: "lower", label: "A lowercase letter", ok: /[a-z]/.test(password) },
    { id: "digit", label: "A number", ok: /[0-9]/.test(password) },
    {
      id: "special",
      label: "A special character",
      ok: [...password].some((ch) => SPECIAL_CHARS.includes(ch)),
    },
  ];
}

/** True when `password` satisfies every policy rule. */
export function isPasswordValid(password: string): boolean {
  return evaluatePasswordRules(password).every((rule) => rule.ok);
}
